import { MAX_IMAGE_FILE_SIZE_BYTES } from "components/common/imageUploadConstraints";

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;

const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
};

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));

/**
 * Downscales + re-encodes an oversized image client-side before the server's 2MB cap rejects it
 * outright — bug-report screenshots from retina/4K displays are commonly multi-MB PNGs. Skips
 * HEIC/HEIF (most browsers can't decode those into a canvas); those fall back to the hard size
 * check at the call site. Returns the original file unchanged if it's already small enough, or
 * if compression fails for any reason.
 */
export const compressImageIfNeeded = async (file: File): Promise<File> => {
  if (file.size <= MAX_IMAGE_FILE_SIZE_BYTES) return file;
  if (file.type === "image/heic" || file.type === "image/heif") return file;

  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const width = Math.round(img.width * scale);
    const height = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await canvasToBlob(canvas);
    if (!blob) return file;

    const newName = file.name.replace(/\.\w+$/, ".jpg");
    return new File([blob], newName, { type: "image/jpeg" });
  } catch {
    return file;
  }
};
